import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePerformance } from '../hooks/usePerformance';
import { SynonymRotator } from './TextEffects';

const Cta = () => {
    const ctaRef = useRef(null);
    const { isMobile, prefersReducedMotion, isLowEndDevice } = usePerformance();

    return ( 
        <div 
            ref={ctaRef}
            className="w-full flex items-center justify-center text-white dark:text-white bg-gray-900 dark:bg-black min-h-screen transition-colors relative overflow-hidden py-20"
        >
            {/* Vibrant animated background - optimized for mobile with better visibility */}
            {!prefersReducedMotion && (() => {
                const orbCount = isMobile || isLowEndDevice ? 3 : 6;
                const blurAmount = isMobile || isLowEndDevice ? '50px' : '90px';
                
                return (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        {/* Dynamic light orbs - more visible on dark background */}
                        {Array.from({ length: orbCount }).map((_, i) => {
                            const baseOpacity = i % 3 === 0 ? 0.6 : i % 3 === 1 ? 0.5 : 0.55;
                            return (
                                <motion.div
                                    key={i}
                                    className="absolute rounded-full"
                                    style={{
                                        width: `${250 + i * 100}px`,
                                        height: `${250 + i * 100}px`,
                                        background: `radial-gradient(circle, ${
                                            i % 3 === 0 
                                                ? 'rgba(139, 92, 246, 0.4)' 
                                                : i % 3 === 1
                                                ? 'rgba(59, 130, 246, 0.35)'
                                                : 'rgba(236, 72, 153, 0.4)'
                                        } 0%, transparent 70%)`,
                                        filter: `blur(${blurAmount})`,
                                        left: `${3 + i * 12}%`,
                                        top: `${5 + i * 10}%`,
                                        opacity: baseOpacity,
                                        willChange: 'transform, opacity',
                                        contain: 'layout style paint',
                                        transform: 'translateZ(0)',
                                    }}
                                    animate={{
                                        x: [0, isMobile ? 40 : 70, isMobile ? -40 : -70, 0],
                                        y: [0, isMobile ? -40 : -70, isMobile ? 40 : 70, 0],
                                        scale: isMobile ? [1, 1.2, 0.8, 1] : [1, 1.5, 0.6, 1],
                                        opacity: [
                                            baseOpacity,
                                            baseOpacity + (isMobile ? 0.2 : 0.25),
                                            baseOpacity - (isMobile ? 0.2 : 0.25),
                                            baseOpacity,
                                        ],
                                    }}
                                    transition={{
                                        duration: 14 + i * 2,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                        delay: i * 1.5,
                                        type: "tween",
                                    }}
                                />
                            );
                        })}
                    </div>
                );
            })()}
            <div className="max-w-7xl mx-auto w-full text-center lg:text-left py-16 px-8 md:px-12 relative z-10">                    
                <div 
                    className="w-full flex flex-col lg:flex-row lg:justify-between lg:items-center gap-8"
                >
                    <div 
                        className="mb-8 lg:mb-0 lg:flex-1"
                    >
                        <p 
                            className='text-3xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight'
                        >
                            <span className="text-white">Avez-vous </span>
                            <SynonymRotator 
                                synonyms={[
                                    'une campagne publicitaire', 
                                    'un projet marketing', 
                                    'une stratégie média', 
                                    'un lancement de produit',
                                    'une initiative digitale',
                                    'une activation de marque',
                                    'un événement promotionnel',
                                    'une campagne d\'influence',
                                    'une opération spéciale'
                                ]}
                                className="text-3xl md:text-5xl lg:text-6xl font-black text-white"
                                interval={2000}
                                typingSpeed={50}
                            />
                            <span className="text-white"> en cours ?</span>
                        </p>
                        <p 
                            className="text-lg md:text-xl font-light"
                        >
                            <span className="text-gray-300">Vous cherchez des conseils, un regard extérieur, une deuxième offre, ou même un plan média plus </span>
                            <span className="text-gray-300 font-semibold">optimisé</span>
                            <span className="text-gray-300"> ? Nous sommes là pour vous écouter et vous assister à chaque étape de votre projet.</span>
                        </p>
                    </div>
                    
                    <div 
                        className="w-full lg:w-auto pt-6 lg:pt-0 lg:ml-12"
                    >
                        <Link 
                            to="/contact" 
                            className="bg-white dark:bg-white text-gray-900 dark:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-200 justify-center text-center px-10 py-4 flex items-center group font-semibold tracking-wide transition-all duration-300"
                        >
                            Envoyer un message
                            <motion.svg 
                                className="w-5 h-5 ml-2" 
                                xmlns="http://www.w3.org/2000/svg" 
                                viewBox="0 0 20 20" 
                                fill="currentColor"
                                animate={{ x: [0, 5, 0] }}
                                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"></path>
                            </motion.svg>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
     );
}
 
export default Cta;
